import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { User, LogOut, CalendarCheck, ChevronDown, Menu, X, Search, Globe } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const NAV = [
  {
    label: "Rent a Car", testId: "nav-rent", items: [
      { to: "/rent-a-car", label: "Short-Term Rental" },
      { to: "/monthly", label: "Monthly Rental" },
      { to: "/international", label: "International Rentals" },
    ],
  },
  {
    label: "Why Europcar", testId: "nav-why", items: [
      { to: "/fleet", label: "Our Fleet" },
      { to: "/network", label: "Our Network & Branches" },
      { to: "/locations", label: "Locations & Branch Finder" },
      { to: "/promise", label: "The Europcar Promise" },
      { to: "/renters-guide", label: "Renter's Guide" },
    ],
  },
  { label: "Partner With Us", to: "/partner-with-us", testId: "nav-partners" },
  { label: "Offers", to: "/offers", testId: "nav-offers" },
  { label: "Blog", to: "/blog", testId: "nav-blog" },
  {
    label: "Help", testId: "nav-help", items: [
      { to: "/help", label: "Help & FAQs" },
      { to: "/contact", label: "Contact Us" },
    ],
  },
];

const NavDropdown = ({ item }) => {
  const { t } = useTranslation();
  return (
    <div className="relative group">
      <button className="flex items-center gap-1 hover:text-[#009900] transition-colors py-5" data-testid={`${item.testId}-menu`}>
        {t(item.label)} <ChevronDown className="w-3.5 h-3.5" />
      </button>
      <div className="absolute left-0 top-full hidden group-hover:block bg-white/95 backdrop-blur-xl border border-[#e2ebe4] rounded-xl shadow-lg ring-1 ring-black/5 py-2 min-w-[248px] z-50">
        {item.items.map((sub) => (
          <Link key={sub.to} to={sub.to} className="block px-4 py-2.5 text-xs normal-case tracking-normal font-medium text-[#144733] hover:bg-[#f0f9f0] hover:text-[#009900] transition-colors"
            data-testid={`nav-sub-${sub.to.replace("/", "")}`}>
            {t(sub.label)}
          </Link>
        ))}
      </div>
    </div>
  );
};

export const Header = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { lang, toggle } = useLanguage();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#e6ece7]/80" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <img src="/europcar-logo.jpg" alt="Europcar" className="h-10 w-auto rounded" />
          <span className="hidden xl:inline-block text-[10px] uppercase tracking-widest text-[#144733] font-semibold border-l border-[#d5ddd5] pl-3">
            {t("Dubai & Northern Emirates")}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 text-xs font-medium uppercase tracking-wide text-[#144733]">
          {NAV.map((item) => item.items
            ? <NavDropdown key={item.label} item={item} />
            : <Link key={item.label} to={item.to} className="hover:text-[#009900] transition-colors" data-testid={item.testId}>{t(item.label)}</Link>)}
          <Link to="/about" className="hover:text-[#009900] transition-colors" data-testid="nav-about">{t("About")}</Link>
        </nav>

        <div className="flex items-center gap-3">
          <button onClick={toggle}
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#144733] hover:text-[#009900] transition-colors border border-[#d5ddd5] rounded-full px-2.5 py-1"
            aria-label="Switch language" data-testid="lang-toggle-btn">
            <Globe className="w-3.5 h-3.5" /> {lang === "ar" ? "EN" : "ع"}
          </button>
          <button onClick={() => navigate("/search")} className="text-[#144733] hover:text-[#009900] transition-colors" aria-label="Search the site" data-testid="header-search-btn">
            <Search className="w-5 h-5" />
          </button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 text-sm font-medium text-[#144733] hover:text-[#009900] transition-colors" data-testid="user-menu-btn">
                  <span className="w-8 h-8 rounded-full bg-[#009900] text-white flex items-center justify-center text-xs font-bold">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </span>
                  <span className="hidden sm:inline max-w-[120px] truncate">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-my-bookings">
                  <CalendarCheck className="w-4 h-4 mr-2" /> {t("Account & Vehicle")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> {t("Sign Out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button onClick={() => navigate("/login")} className="btn-cta rounded px-4 py-2 text-xs sm:text-sm flex items-center gap-2" data-testid="header-signin-btn">
              <User className="w-4 h-4" /> {t("Sign In")}
            </button>
          )}
          <button className="lg:hidden text-[#144733]" onClick={() => setMobileOpen((o) => !o)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[#e6ece7] px-4 py-4 space-y-1 max-h-[70vh] overflow-y-auto" data-testid="mobile-nav">
          {NAV.flatMap((item) => item.items ? item.items : [item]).map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
              className="block py-2.5 text-sm font-medium uppercase tracking-wide text-[#144733] hover:text-[#009900] border-b border-[#f1f3f1]">
              {t(l.label)}
            </Link>
          ))}
          <Link to="/about" onClick={() => setMobileOpen(false)} className="block py-2.5 text-sm font-medium uppercase tracking-wide text-[#144733]">{t("About Us")}</Link>
          <Link to="/account" onClick={() => setMobileOpen(false)} className="block py-2.5 text-sm font-medium uppercase tracking-wide text-[#009900]">{t("Account / Vehicle")}</Link>
        </div>
      )}
    </header>
  );
};
